import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, User, Phone, CreditCard, Calendar, BookOpen, Calculator, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_OPTIONS = [
  { 
    id: "bq_abo", 
    label: "BQ-Abonnement", 
    description: "Laufzeit 1-12 Monate",
    icon: CreditCard,
    hasLaufzeit: true,
    category: "abo"
  },
  { 
    id: "hq_abo", 
    label: "HQ-Abonnement", 
    description: "Laufzeit 1-12 Monate, Fachrichtung wählen",
    icon: CreditCard,
    hasLaufzeit: true,
    hasHqSubType: true,
    category: "abo"
  },
  { 
    id: "bq_intensiv", 
    label: "BQ-Intensivkurs", 
    description: "Laufzeit 1-12 Monate, Fächer wählen.",
    icon: BookOpen,
    category: "intensiv",
    hasLaufzeit: true,
    hasBqSubjects: true
  },
  { 
    id: "hq_intensiv", 
    label: "HQ-Intensivkurs", 
    description: "Laufzeit 1-12 Monate, HQ-Fächer wählen.",
    icon: BookOpen,
    category: "intensiv",
    hasLaufzeit: true,
    hasHqSubjects: true
  },
  { 
    id: "probetraining", 
    label: "Probetraining", 
    description: "Einmaliger Zugang am gewählten Tag",
    icon: Calendar,
    hasProbetraining: true,
    category: "probe"
  },
  { 
    id: "mathe_grundkurs", 
    label: "Mathe-Grundkurs", 
    description: "Laufzeit 1-12 Monate, Zugriff auf Mathe-Inhalte",
    icon: Calculator,
    hasLaufzeit: true,
    category: "mathe"
  },
  { 
    id: "fachgespraech", 
    label: "Fachgespräch", 
    description: "Laufzeit 1-12 Monate, Vorbereitung Fachgespräch",
    icon: MessageCircle,
    hasLaufzeit: true,
    category: "fach"
  },
];

const BQ_SUBJECTS = [
  { id: "NTG", label: "NTG - Naturwissenschaftliche und technische Grundlagen" },
  { id: "BWH", label: "BWH - Betriebswirtschaftliches Handeln" },
  { id: "ZIB", label: "ZIB - Zusammenarbeit im Betrieb" },
  { id: "RBH", label: "RBH - Rechtsbewusstes Handeln" },
  { id: "MIKP", label: "MIKP - Methoden der Information, Kommunikation und Planung" },
];

const HQ_INTENSIV_SUBJECTS = [
  { id: "orga", label: "Organisation" },
  { id: "betriebliches_kostenwesen", label: "Betriebliches Kostenwesen" },
  { id: "montagetechnik", label: "Montagetechnik" },
  { id: "fertigungstechnik", label: "Fertigungstechnik" },
  { id: "betriebstechnik", label: "Betriebstechnik" },
];

const HQ_SUB_TYPES = [
  { id: "metall", label: "HQ Abo Metalltechnik" },
];

const PROBETRAINING_SUBJECTS = [
  "NTG", "BWH", "ZIB", "RBH", "MIKP",
  "HQ Abo Metalltechnik"
];

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    industriemeister_fachrichtung: "",
  });
  
  const [selectedBookings, setSelectedBookings] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleBooking = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev[bookingId]) {
        const { [bookingId]: removed, ...rest } = prev;
        return rest;
      }
      
      // Default values for new booking
      const booking = BOOKING_OPTIONS.find(b => b.id === bookingId);
      const newBooking = { booking_type: bookingId };
      
      if (booking?.hasLaufzeit) {
        newBooking.duration_months = 1;
        newBooking.start_date = new Date().toISOString().split("T")[0];
      }
      if (booking?.hasHqSubType) {
        newBooking.hq_sub_type = "metall";
      }
      if (booking?.hasProbetraining) {
        newBooking.probetraining_subject = "NTG";
        newBooking.probetraining_date = new Date().toISOString().split("T")[0];
      }
      if (booking?.hasBqSubjects) {
        newBooking.subjects = [];
      }
      if (booking?.hasHqSubjects) {
        newBooking.subjects = [];
      }
      
      return { ...prev, [bookingId]: newBooking };
    });
  };

  const updateBookingDetail = (bookingId, field, value) => {
    setSelectedBookings(prev => ({
      ...prev,
      [bookingId]: { ...prev[bookingId], [field]: value }
    }));
  };

  const toggleSubject = (bookingId, subject) => {
    setSelectedBookings(prev => {
      const current = prev[bookingId]?.subjects || [];
      const updated = current.includes(subject) 
        ? current.filter(s => s !== subject) 
        : [...current, subject];
      return { ...prev, [bookingId]: { ...prev[bookingId], subjects: updated } };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.password || !formData.industriemeister_fachrichtung) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }

    const bookings = Object.values(selectedBookings);
    if (bookings.length === 0) {
      toast.error("Bitte mindestens eine Buchung auswählen");
      return;
    }

    // Validate subjects for intensiv courses
    for (const b of bookings) {
      if ((b.booking_type === "bq_intensiv" || b.booking_type === "hq_intensiv") && (!b.subjects || b.subjects.length === 0)) {
        toast.error(`Bitte mindestens ein Fach für ${b.booking_type === "bq_intensiv" ? "BQ-Intensiv" : "HQ-Intensiv"} wählen`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        industriemeister_fachrichtung: formData.industriemeister_fachrichtung,
        bookings: bookings
      };

      const response = await axios.post(`${API}/auth/register`, payload);
      const { token } = response.data;
      
      localStorage.setItem("token", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      
      toast.success("Registrierung erfolgreich!");
      navigate("/dashboard");
      window.location.reload();
    } catch (error) {
      const message = error.response?.data?.detail || "Registrierung fehlgeschlagen";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = Object.keys(selectedBookings).length;

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/logo-st.webp" 
            alt="S&T Brain-Trainer" 
            className="h-24 w-auto object-contain mx-auto mb-4"
            data-testid="register-logo"
          />
          <h1 className="text-3xl font-bold text-white tracking-tight">Brain-Trainer Registrierung</h1>
          <p className="text-muted-foreground mt-2">Erstellen Sie Ihr Konto und wählen Sie Ihre Buchungen</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Data */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Persönliche Daten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Vollständiger Name *</Label>
                  <Input
                    id="name"
                    placeholder="Max Mustermann"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className="bg-background"
                    data-testid="register-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefonnummer *</Label>
                  <Input
                    id="phone"
                    placeholder="+49 123 456789"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    className="bg-background"
                    data-testid="register-phone"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@beispiel.de"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="bg-background"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fachrichtung">Industriemeister-Fachrichtung *</Label>
                <Input
                  id="fachrichtung"
                  placeholder="z.B. Metall, Elektrotechnik, Logistik, Lebensmittel..."
                  value={formData.industriemeister_fachrichtung}
                  onChange={(e) => handleChange("industriemeister_fachrichtung", e.target.value)}
                  className="bg-background"
                  data-testid="register-fachrichtung"
                />
                <p className="text-xs text-muted-foreground">Freitextfeld - geben Sie Ihre Fachrichtung ein</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort *</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    className="bg-background"
                    data-testid="register-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    className="bg-background"
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Booking Selection */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                Buchungen auswählen
                {selectedCount > 0 && (
                  <span className="ml-2 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-sm">
                    {selectedCount} ausgewählt
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                Wählen Sie eine oder mehrere Buchungen. Mehrfachbuchungen sind möglich.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {BOOKING_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = !!selectedBookings[option.id];
                const bookingData = selectedBookings[option.id];
                
                return (
                  <div 
                    key={option.id}
                    className={`p-4 rounded-sm border transition-colors ${
                      isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id={option.id}
                        checked={isSelected}
                        onCheckedChange={() => toggleBooking(option.id)}
                        className="mt-1"
                        data-testid={`booking-${option.id}`}
                      />
                      <div className="flex-1">
                        <label htmlFor={option.id} className="flex items-center gap-2 cursor-pointer">
                          <Icon className="h-5 w-5 text-primary" />
                          <span className="font-medium">{option.label}</span>
                        </label>
                        <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                        
                        {/* Additional options when selected */}
                        {isSelected && (
                          <div className="mt-4 pl-7 space-y-4">
                            {/* HQ Sub-Type Selection */}
                            {option.hasHqSubType && (
                              <div className="space-y-2">
                                <Label>HQ-Fachrichtung</Label>
                                <Select
                                  value={bookingData?.hq_sub_type || "metall"}
                                  onValueChange={(v) => updateBookingDetail(option.id, "hq_sub_type", v)}
                                >
                                  <SelectTrigger className="bg-background w-full md:w-64" data-testid={`hq-subtype-${option.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {HQ_SUB_TYPES.map(st => (
                                      <SelectItem key={st.id} value={st.id}>{st.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            
                            {/* Laufzeit for Abos */}
                            {option.hasLaufzeit && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Startdatum</Label>
                                  <Input
                                    type="date"
                                    value={bookingData?.start_date || new Date().toISOString().split("T")[0]}
                                    onChange={(e) => updateBookingDetail(option.id, "start_date", e.target.value)}
                                    className="bg-background"
                                    data-testid={`start-date-${option.id}`}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Laufzeit (Monate)</Label>
                                  <Select
                                    value={(bookingData?.duration_months || 1).toString()}
                                    onValueChange={(v) => updateBookingDetail(option.id, "duration_months", parseInt(v))}
                                  >
                                    <SelectTrigger className="bg-background" data-testid={`duration-${option.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                        <SelectItem key={m} value={m.toString()}>{m} {m === 1 ? "Monat" : "Monate"}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            )}
                            
                            {/* Probetraining options */}
                            {option.hasProbetraining && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Fach</Label>
                                  <Select
                                    value={bookingData?.probetraining_subject || "NTG"}
                                    onValueChange={(v) => updateBookingDetail(option.id, "probetraining_subject", v)}
                                  >
                                    <SelectTrigger className="bg-background" data-testid={`probe-subject-${option.id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {PROBETRAINING_SUBJECTS.map(s => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-2">
                                  <Label>Datum des Probetrainings</Label>
                                  <Input
                                    type="date"
                                    value={bookingData?.probetraining_date || new Date().toISOString().split("T")[0]}
                                    onChange={(e) => updateBookingDetail(option.id, "probetraining_date", e.target.value)}
                                    className="bg-background"
                                    data-testid={`probe-date-${option.id}`}
                                  />
                                  <p className="text-xs text-muted-foreground">Zugriff nur an diesem Tag (00:00 - 23:59)</p>
                                </div>
                              </div>
                            )}

                            {/* BQ-Intensiv: Subject Selection */}
                            {option.hasBqSubjects && (
                              <div className="space-y-3">
                                <Label className="font-medium">BQ-Fächer auswählen <span className="text-destructive">*</span></Label>
                                <p className="text-xs text-muted-foreground">Diese Fächer müssen vom Admin freigegeben werden.</p>
                                <div className="grid grid-cols-1 gap-2">
                                  {BQ_SUBJECTS.map((subj) => {
                                    const checked = bookingData?.subjects?.includes(subj.id) || false;
                                    return (
                                      <label
                                        key={subj.id}
                                        className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                                          checked ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/30"
                                        }`}
                                        data-testid={`bq-subject-${subj.id}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleSubject(option.id, subj.id)}
                                          className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm">{subj.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* HQ-Intensiv: Subject Selection */}
                            {option.hasHqSubjects && (
                              <div className="space-y-3">
                                <Label className="font-medium">HQ-Fächer auswählen <span className="text-destructive">*</span></Label>
                                <p className="text-xs text-muted-foreground">Diese Fächer müssen vom Admin freigegeben werden.</p>
                                <div className="grid grid-cols-1 gap-2">
                                  {HQ_INTENSIV_SUBJECTS.map((subj) => {
                                    const checked = bookingData?.subjects?.includes(subj.id) || false;
                                    return (
                                      <label
                                        key={subj.id}
                                        className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-colors ${
                                          checked ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/30"
                                        }`}
                                        data-testid={`hq-subject-${subj.id}`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleSubject(option.id, subj.id)}
                                          className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                                        />
                                        <span className="text-sm">{subj.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium uppercase tracking-wide py-6 text-lg"
              disabled={loading || selectedCount === 0}
              data-testid="register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Registrierung läuft...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Registrieren mit {selectedCount} Buchung{selectedCount !== 1 ? "en" : ""}
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </Button>
            
            <p className="text-center text-sm text-muted-foreground">
              Bereits registriert?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Anmelden
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
